/**
 * API Implementation of IStudyService
 *
 * Real BE: getPageData, getDiagnosisQuizSession, submitDiagnosisAnswer + 4 subject quiz APIs.
 * Other methods delegate to mockStudyService.
 */

import { clientApi } from "@/utils/api";
import { TApiRes } from "@/types/api";
import {
  TStudyPageData,
  TStudySubject,
  TDiagnosisQuizSession,
  TDiagnosisQuestion,
  TDiagnosisQuizMetadata,
  TDiagnosisAnswerRequest,
  TDiagnosisAnswerResult,
  TSubjectLevelsResponse,
  TSubjectQuizSession,
  TSubjectQuizSessionDTO,
  TSubjectQuizAnswerRequest,
  TSubjectQuizAnswerResponse,
  TSubjectQuizResult,
  TWordPracticeSession,
  TWordPracticeItem,
  TStudyVocabQuizSession,
  TStudyVocabQuizSessionDTO,
  TStudyVocabQuizAnswerRequest,
  TStudyVocabQuizAnswerResponse,
  TStudyVocabQuizResult,
  TSubjectQuizPracticeSession,
  TSubjectQuizPracticeAnswerRequest,
  TSubjectQuizPracticeAnswerResponse,
  TSubjectQuizPracticeResult,
  TRecommendation,
} from "@/infrastructure/adapters/study/study.types";
import { IStudyService } from "@/infrastructure/services/study/study.service";
import { mockStudyService } from "@/infrastructure/services/study/study.service.mock";
import { parseRecentResults } from "@/infrastructure/adapters/vocabulary/vocabulary.mastery";

/* ── DTO types for BE responses ── */

type TPublishedSubjectsResponse = {
  data: {
    isTrial: boolean;
    subjects: {
      id: string;
      name: string;
      displayOrder: number;
      hasAnswered: boolean;
      isCompleted: boolean;
      levelAssessment: string | null;
      isLocked: boolean;
    }[];
  };
};

type TLevelAssessmentResponse = {
  data: {
    sessionId: string;
    totalQuestions: number;
    questions: {
      sessionItemId: string;
      questionId: number;
      questionText: string;
      translatedQuestionText: string | null;
      imageFile: string | null;
      difficulty: string;
      explanation: string;
      translatedExplanation: string | null;
      options: { index: number; text: string; translatedText: string | null }[];
    }[];
  };
};

type TLevelAssessmentQuestions = {
  data: {
    totalQuestions: number;
  };
};

type TVocabularyDTO = {
  id: number;
  word: string;
  furigana: string | null;
  imageFile?: string | null;
  /** Signed URL pre-resolved by BE (valid 30 min) */
  imageUrl?: string | null;
  explanation: string;
  learnedCount: number;
};

/* ── Mappers ── */

const mapSubject = (raw: TPublishedSubjectsResponse["data"]["subjects"][number]): TStudySubject => ({
  id: raw.id,
  name: raw.name,
  isCompleted: raw.isCompleted,
  hasAnswered: raw.hasAnswered,
  hasLevelAssessment: !!raw.levelAssessment,
  isLocked: raw.isLocked ?? false,
});

const mapDiagnosisQuestion = (raw: TLevelAssessmentResponse["data"]["questions"][number]): TDiagnosisQuestion => ({
  questionText: raw.questionText,
  translatedQuestionText: raw.translatedQuestionText ?? null,
  imageFile: raw.imageFile ?? undefined,
  options: raw.options.sort((a, b) => a.index - b.index).map((o) => ({ text: o.text, translatedText: o.translatedText ?? null })),
  level: parseInt(raw.difficulty, 10) || 1,
  explanation: raw.explanation,
  translatedExplanation: raw.translatedExplanation ?? null,
  sessionItemId: raw.sessionItemId,
  questionId: raw.questionId,
});

export const apiStudyService: IStudyService = {
  /* ── Real BE: getPageData, diagnosis ── */

  async getPageData(): Promise<TStudyPageData> {
    const { data } = await clientApi.get<TPublishedSubjectsResponse>("/learning/published-subjects");
    const subjects = data.data.subjects.sort((a, b) => a.displayOrder - b.displayOrder).map(mapSubject);
    return {
      subjects,
      isTrial: data.data.isTrial ?? false,
      recommendation: null,
    };
  },

  async getDiagnosisQuizSession(subjectId: string): Promise<TDiagnosisQuizSession> {
    const { data } = await clientApi.get<TLevelAssessmentResponse>(`/learning/subject/${subjectId}/level-assessment-questions`);
    return {
      sessionId: data.data.sessionId,
      questions: data.data.questions.map(mapDiagnosisQuestion),
      totalCount: data.data.totalQuestions,
    };
  },

  async getDiagnosisQuizMetadata(subjectId: string): Promise<TDiagnosisQuizMetadata> {
    const { data } = await clientApi.get<TLevelAssessmentQuestions>(`/learning/subject/${subjectId}/level-assessment-questions/count`);
    return {
      totalQuestions: data.data.totalQuestions,
    };
  },

  async submitDiagnosisAnswer(sessionId: string, answer: TDiagnosisAnswerRequest): Promise<TDiagnosisAnswerResult> {
    const { data } = await clientApi.post<{ data: TDiagnosisAnswerResult }>(`/learning/level-assessment/session/${sessionId}/answers`, answer);
    return data.data;
  },

  /* ── Real BE: 4 subject quiz APIs ── */

  getSubjectLevels: async (subjectId: string): Promise<TSubjectLevelsResponse> => {
    const res = await clientApi.get<TApiRes<TSubjectLevelsResponse>>(`/learning/subjects/${subjectId}/levels`);
    return res.data.data;
  },

  createSubjectQuizSession: async (subjectId: string, level: string, limit: number): Promise<TSubjectQuizSession> => {
    const res = await clientApi.post<TApiRes<TSubjectQuizSessionDTO>>(`/learning/subjects/${subjectId}/quiz/sessions`, { level, limit });
    const dto = res.data.data;
    return {
      quizSessionId: dto.quizSessionId,
      totalQuestions: dto.totalQuestions,
      questions: dto.questions.map((q) => ({
        sessionItemId: q.sessionItemId,
        questionId: q.questionId,
        questionText: q.questionText,
        imageFile: q.imageFile,
        choices: q.choices.map((c) => ({ choiceId: c.choiceId, text: c.text })),
        recentResults: parseRecentResults(q.recentResults),
        masteryCorrectCount: q.masteryCorrectCount ?? 0,
      })),
    };
  },

  submitSubjectQuizAnswer: async (quizSessionId: string, req: TSubjectQuizAnswerRequest): Promise<TSubjectQuizAnswerResponse> => {
    const res = await clientApi.post<TApiRes<TSubjectQuizAnswerResponse>>(`/learning/subject-quiz/sessions/${quizSessionId}/answers`, req);
    return res.data.data;
  },

  getSubjectQuizResult: async (quizSessionId: string): Promise<TSubjectQuizResult> => {
    const res = await clientApi.get<TApiRes<TSubjectQuizResult>>(`/learning/subject-quiz/sessions/${quizSessionId}/result`);
    const dto = res.data.data;
    return {
      quizSessionId: dto.quizSessionId,
      correctCount: dto.correctCount,
      wrongCount: dto.wrongCount,
      totalQuestions: dto.totalQuestions,
      accuracyRate: dto.accuracyRate,
      items: dto.items.map((item) => ({
        questionId: item.questionId,
        displayOrder: item.displayOrder,
        questionText: item.questionText,
        translatedQuestionText: item.translatedQuestionText ?? null,
        imageFile: item.imageFile,
        isCorrect: item.isCorrect,
        selectedOptionId: item.selectedOptionId,
        correctOptionId: item.correctOptionId,
        selectedOptionText: item.selectedOptionText,
        correctOptionText: item.correctOptionText,
        selectedOptionTranslatedText: item.selectedOptionTranslatedText ?? null,
        correctOptionTranslatedText: item.correctOptionTranslatedText ?? null,
        explanation: item.explanation,
        translatedExplanation: item.translatedExplanation ?? null,
        options: item.options.map((o) => ({ optionId: o.optionId, text: o.text, translatedText: o.translatedText ?? null })),
      })),
      answeredCount: dto.answeredCount,
      isCompleted: dto.isCompleted,
    };
  },

  /* ── Real BE: Vocabulary practice ── */

  async getWordPracticeSession(subjectId: string): Promise<TWordPracticeSession> {
    const res = await clientApi.get<TApiRes<TVocabularyDTO[]>>(`/learning/subjects/${subjectId}/vocabularies`);
    const vocabularies = res.data.data;
    const words: TWordPracticeItem[] = vocabularies.map((v) => ({
      id: v.id,
      kanji: v.word,
      furigana: v.furigana ?? "",
      translation: v.explanation,
      imageFile: v.imageFile ?? null,
      imageUrl: v.imageUrl ?? null,
    }));
    return { words, totalCount: words.length };
  },

  async updateVocabularyProgress(subjectId: string, vocabularyId: number): Promise<boolean> {
    const res = await clientApi.patch<TApiRes<boolean>>(`/learning/subjects/${subjectId}/vocabularies/${vocabularyId}/progress`);
    return res.data.data;
  },

  /* ── Real BE: Vocabulary quiz (3 APIs) ── */

  async createVocabularyQuizSession(subjectId: string, limit: number): Promise<TStudyVocabQuizSession> {
    const res = await clientApi.post<TApiRes<TStudyVocabQuizSessionDTO>>(`/learning/subjects/${subjectId}/vocabulary-quiz/sessions`, { limit });
    const dto = res.data.data;
    return {
      ...dto,
      questions: dto.questions.map((q) => ({
        ...q,
        recentResults: parseRecentResults(q.recentResults),
        masteryCorrectCount: q.masteryCorrectCount ?? 0,
      })),
    };
  },

  async submitVocabularyQuizAnswer(quizSessionId: string, req: TStudyVocabQuizAnswerRequest): Promise<TStudyVocabQuizAnswerResponse> {
    const res = await clientApi.post<TApiRes<TStudyVocabQuizAnswerResponse>>(
      `/learning/subject-vocabulary-quiz/sessions/${quizSessionId}/answers`,
      req,
    );
    return res.data.data;
  },

  async getVocabularyQuizResult(quizSessionId: string): Promise<TStudyVocabQuizResult> {
    const res = await clientApi.get<TApiRes<TStudyVocabQuizResult>>(`/learning/subject-vocabulary-quiz/sessions/${quizSessionId}/result`);
    return res.data.data;
  },

  async skipLevelAssessment(subjectId: string): Promise<{ subjectId: number; level: string }> {
    const res = await clientApi.post<TApiRes<{ subjectId: number; level: string }>>(`/learning/subjects/${subjectId}/skip-level-assessment`);
    return res.data.data;
  },

  /* ── Real BE: Subject Quiz Practice (3 APIs) ── */

  async createSubjectQuizPracticeSession(subjectId: string, level: string, limit?: number): Promise<TSubjectQuizPracticeSession> {
    const res = await clientApi.post<TApiRes<TSubjectQuizPracticeSession>>(`/learning/subjects/${subjectId}/quiz-practice/sessions`, {
      level,
      ...(limit ? { limit } : {}),
    });
    const dto = res.data.data;
    return {
      quizSessionId: dto.quizSessionId,
      totalQuestions: dto.totalQuestions,
      questions: dto.questions.map((q) => ({
        sessionItemId: q.sessionItemId,
        questionId: q.questionId,
        questionText: q.questionText,
        translatedQuestionText: q.translatedQuestionText ?? null,
        imageFile: q.imageFile,
        choices: q.choices.map((c) => ({
          choiceId: c.choiceId,
          text: c.text,
          translatedText: c.translatedText ?? null,
        })),
      })),
    };
  },

  async submitSubjectQuizPracticeAnswer(quizSessionId: string, req: TSubjectQuizPracticeAnswerRequest): Promise<TSubjectQuizPracticeAnswerResponse> {
    const res = await clientApi.post<TApiRes<TSubjectQuizPracticeAnswerResponse>>(
      `/learning/subject-quiz-practice/sessions/${quizSessionId}/answers`,
      req,
    );
    const dto = res.data.data;
    return {
      ...dto,
      translatedExplanation: dto.translatedExplanation ?? null,
    };
  },

  async getSubjectQuizPracticeResult(quizSessionId: string): Promise<TSubjectQuizPracticeResult> {
    const res = await clientApi.get<TApiRes<TSubjectQuizPracticeResult>>(`/learning/subject-quiz-practice/sessions/${quizSessionId}/result`);
    return res.data.data;
  },

  /* ── Real BE: Recommendation ── */
  async getRecommendation(): Promise<TRecommendation | null> {
    const { data } = await clientApi.get<TApiRes<{ recommendation: TRecommendation | null }>>("/learning/recommendations");
    return data.data.recommendation;
  },

  /* ── Real BE: Boost message ── */

  async getBoostMessage(): Promise<string | null> {
    const res = await clientApi.get<TApiRes<{ message: string | null }>>("/learning/boost-message");
    return res.data.data?.message ?? null;
  },

  /* ── Delegate to mock for remaining methods ── */

  getWordQuizSession: mockStudyService.getWordQuizSession,
  getLevelPracticeSession: mockStudyService.getLevelPracticeSession,
  getTextExplanationSession: mockStudyService.getTextExplanationSession,
};
